import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { fields } from 'components/explore/resultsGrid/gridHelpers/fields';

export const initialState = {
  resultsGridDataUpdate: null,
  selectedIdsContext: null,
  propertyDetailsData: null,
  propertyUnderwritesData: null,
  selectedPropertyUnderwrite: null,
  searchContext: null,
  pushProperties: [],
  chartsBucketsData: null,
  propertyDataObjects: {},
  isFetchingPropertyUWHistory: false
}

const propertyDataObjectState = {
  isAggregateData: true,
  groupLevel: null,
  requestedGroupLevel: 'auto',
  propertyData: [],
  propertiesCount: null,
  filter: null,
  filterGeo: null,
  savedFilterId: null,
  fromSearch: false,
  pinsAreVisible: true,
  requestedPinDisplayType: null,
  pinDisplayType: null,
  pinColorPaletteId: 'shapeDefault',
  isMainIndicatorVisible: false,
  isSecondaryIndicatorVisible: false,
  isErrored: false
}

// create slice will call createAction and createReducer for us internally
const slice = createSlice({
  name: 'exploreDataSlice',
  initialState,
  reducers: {
    createExplorePropertyDataObjectInstance: (state, action) => {
      const obj = action.payload;
      state.propertyDataObjects[obj.id] = {
        ...propertyDataObjectState,
        pinsAreVisible: obj.visible,
        requestedGroupLevel: obj.groupLevel,
        pinDisplayType: obj.pinDisplayType ? fields.find(field => field.dataField === obj.pinDisplayType) : null,
        pinColorPaletteId: obj.pinColorPaletteId,
        pinColorIds: obj.pinColorIds,
        filter: obj.filter,
        filterGeo: obj.filterGeo
      }
    },
    deleteExplorePropertyDataObjectInstance: (state, action) => {
      const id = action.payload;
      delete state.propertyDataObjects[id];
    },
    setExplorePropertyDataObjects: (state, action) => {
      state.propertyDataObjects = action.payload;
    },
    updateExplorePropertyDataObjectById: {
      reducer: (state, action) => {
        const { objectId, update } = action.payload;
        state.propertyDataObjects[objectId] = {
          ...state.propertyDataObjects[objectId],
          ...update
        } 
      },
      prepare: (objectId, update) => ({ payload: { objectId, update } })
    },
    setExploreSelectedIds: (state, action) => { // Move to map slice
      state.selectedIdsContext = action.payload;
    },
    selectExploreMapPin: (state, action) => { // Move to map slice
      const { ctrlKey, objectId, clusterID } = action.payload;
      const selectedPinIds = state.selectedIdsContext ? state.selectedIdsContext.ids : null;
      let newIds = [];
      if (selectedPinIds?.length > 0 && ctrlKey) {
        newIds = [...selectedPinIds];
        let existingIndex = newIds.findIndex(el => el.clusterID === clusterID);
        if (existingIndex === -1) {
          newIds.push({ objectId, clusterID });
        } else {
          newIds.splice(existingIndex, 1);
        }
      } else {
        newIds.push({ objectId, clusterID });
      }
      state.selectedIdsContext = {
        ids: newIds,
        mapSelection: { objectId, clusterID },
        from: 'Map'
      };
    },
    setExplorePropertyDetailsData: (state, action) => {
      state.propertyDetailsData = action.payload;
    },
    setExplorePropertyUnderwritesData: (state, action) => {
      state.propertyUnderwritesData = action.payload;
    },
    setSelectedPropertyUnderwrite: (state, action) => {
      state.selectedPropertyUnderwrite = action.payload;
    },
    setExploreSearch: {
      reducer: (state, action) => {
        state.searchContext = action.payload.searchContext;
      },
      prepare: (searchContext, forceSearchSet = false) => ({ payload: { searchContext, forceSearchSet } })
    },
    setExploreFilter: {
      reducer: (state, action) => {
        state.propertyDataObjects[action.payload.objectId].filter = action.payload.filter;
      },
      prepare: (objectId, filter, filterByImportId = false, shouldFetch = true) => ({ payload: { objectId, filter, filterByImportId, shouldFetch } })
    },
    setExploreGeoFilter: {
      reducer: (state, action) => {
        state.propertyDataObjects[action.payload.objectId].filterGeo = action.payload.filterGeo;
        state.propertyDataObjects[action.payload.objectId].savedFilterId = action.payload.savedFilterId
      },
      prepare: (objectId, filterGeo, shouldFetch = true, savedFilterId) => ({ payload: { objectId, filterGeo, shouldFetch, savedFilterId } })
    },
    setSavedFilterId: (state, action) => {
      state.savedFilterId = action.payload
    },
    setExplorePropertyData: {
      reducer: (state, action) => {
        const { objectId, data, propertiesCount, isAggregateData, groupLevel, fromSearch } = action.payload;
        return {
          ...state,
          propertyDataObjects: {
            ...state.propertyDataObjects,
            [objectId]: {
              ...state.propertyDataObjects[objectId],
              propertyData: data,
              propertiesCount,
              isAggregateData,
              groupLevel,
              fromSearch
            }
          }
        }
      },
      prepare: (objectId, data, propertiesCount, isAggregateData, groupLevel, fromSearch) => ({ payload: { objectId, data, propertiesCount, isAggregateData, groupLevel, fromSearch } })
    },
    setResultsGridDataUpdate: (state, action) => {
      state.resultsGridDataUpdate = action.payload;
    },
    setIsMainDataLoading: {
      reducer: (state, action) => {
        const { objectId, isMainIndicatorVisible } = action.payload;
        return {
          ...state,
          propertyDataObjects: {
            ...state.propertyDataObjects,
            [objectId]: {
              ...state.propertyDataObjects[objectId],
              isMainIndicatorVisible
            }
          }
        }
      },
      prepare: (objectId, isMainIndicatorVisible) => ({ payload: { objectId, isMainIndicatorVisible }})
    },
    setIsSecondaryDataLoading: {
      reducer: (state, action) => {
        const { objectId, isSecondaryIndicatorVisible } = action.payload;
        return {
          ...state,
          propertyDataObjects: {
            ...state.propertyDataObjects,
            [objectId]: {
              ...state.propertyDataObjects[objectId],
              isSecondaryIndicatorVisible
            }
          }
        }
      },
      prepare: (objectId, isSecondaryIndicatorVisible) => ({ payload: { objectId, isSecondaryIndicatorVisible }})
    },
    getExploreSelectedProperties: () => { },
    setExplorePushProperties: (state, action) => {
      state.pushProperties = action.payload;
    },
    setExploreChartsBucketsData: (state, action) => {
      state.chartsBucketsData = action.payload;
    },
    setExplorePinsAreVisible: {
      reducer: (state, action) => {
        const { objectId, pinsAreVisible } = action.payload;
        return {
          ...state,
          propertyDataObjects: {
            ...state.propertyDataObjects,
            [objectId]: {
              ...state.propertyDataObjects[objectId],
              pinsAreVisible
            }
          }
        }
      },
      prepare: (objectId, pinsAreVisible) => ({ payload: { objectId, pinsAreVisible } })
    },
    setExploreRequestedGroupLevel: {
      reducer: (state, action) => {
        state.propertyDataObjects[action.payload.objectId].requestedGroupLevel = action.payload.requestedGroupLevel;
      },
      prepare: (objectId, requestedGroupLevel, shouldFetch) => ({ payload: { objectId, requestedGroupLevel, shouldFetch } })
    },
    setExploreRequestedPinDisplayType: {
      reducer: (state, action) => {
        state.propertyDataObjects[action.payload.objectId].requestedPinDisplayType = action.payload.requestedPinDisplayType;
      },
      prepare: (objectId, requestedPinDisplayType, shouldFetch = true) => ({ payload: { objectId, requestedPinDisplayType, shouldFetch } })
    },
    setExplorePinDisplayType: {
      reducer: (state, action) => {
        state.propertyDataObjects[action.payload.objectId].pinDisplayType = action.payload.pinDisplayType;
      },
      prepare: (objectId, pinDisplayType) => ({ payload: { objectId, pinDisplayType } })
    },
    setPinColorIds: {
      reducer: (state, action) => {
        state.propertyDataObjects[action.payload.objectId].pinColorIds = action.payload.pinColorIds;
      },
      prepare: (objectId, pinColorIds) => ({ payload: { objectId, pinColorIds } })
    },
    setPinColorPaletteId: {
      reducer: (state, action) => {
        state.propertyDataObjects[action.payload.objectId].pinColorPaletteId = action.payload.pinColorPaletteId;
      },
      prepare: (objectId, pinColorPaletteId) => ({ payload: { objectId, pinColorPaletteId } })
    },
    setIsFetchingPropertyUWHistory: (state, action) => {
      state.isFetchingPropertyUWHistory = action.payload;
    },

    // Redux saga actions, no reducers
    fetchExplorePropertyData: () => { }, // Triggers explorePropertyDataSaga
    cancelExploreDataFetch: () => { }, // Triggers explorePropertyDataSaga
    fetchExplorePropertyUnderwrites: { // Triggers explorePropertyDataSaga
      reducer: () => { },
      prepare: (asgPropID, shouldFetchUW = true) => ({ payload: { asgPropID, shouldFetchUW } })
    },
  }
});

export default slice.reducer;

export const exploreDataActions = slice.actions;

// Memoized selectors
export const exploreDataSelectors = {
  exploreDataSelector: createSelector(
    state => state.exploreData,
    exploreData => exploreData
  ),
  propertyDataObjectByIdSelector: createSelector(
    (state, objectId) => state.exploreData.propertyDataObjects[objectId],
    propertyDataObject => propertyDataObject
  ),
  lastSelectedClusterSelector: createSelector(
    [state => state.exploreData.selectedIdsContext?.ids, state => state.exploreData.propertyDataObjects],
    (ids, propertyDataObjects) => {
      if (!ids?.length) return null;
      const lastSelected = ids[ids.length - 1];
      const foundCluster = propertyDataObjects[lastSelected.objectId]?.propertyData?.find(pin => pin.clusterID === lastSelected.clusterID);
      return foundCluster ? { ...foundCluster, objectId: lastSelected.objectId } : null;
    }
  ),
  propertyDataParamsSelector: createSelector(
    [state => state.exploreMap, (state, objectId) => state.exploreData.propertyDataObjects[objectId]],
    (exploreMap, propertyDataObject) => {
      const { bounds, zoom } = exploreMap;
      const { filter, filterGeo, requestedPinDisplayType, pinDisplayType, requestedGroupLevel } = propertyDataObject;
      return {
        requestedGroupLevel: requestedGroupLevel ?? 'auto',
        bounds,
        zoom,
        filter: filter?.filterExpression ? JSON.stringify(filter.filterExpression) : null,
        filterGeo,
        requestedPinDisplayType,
        pinDisplayType
      };
    }
  ),
  exploreSelectedIdsSelector: createSelector(
    state => state.exploreData,
    exploreData => exploreData.selectedIdsContext?.ids
  ),
  exploreSelectedPropertiesCountSelector: createSelector(
    state => state.exploreData,
    exploreData => {
      const { selectedIdsContext, propertyData } = exploreData;
      if (!selectedIdsContext?.ids) return null;
      return propertyData.reduce((sum, data) => sum + (selectedIdsContext.ids.find(id => id === data.clusterID) ? data.propertiesCount : 0), 0)
    }
  ),

  exploreSelectedClusterIDsSelector: createSelector(
    state => state.exploreData,
    exploreData => {
      const { selectedIdsContext, propertyDataObjects } = exploreData;
      const selectedIds = selectedIdsContext?.ids;
      let selectedClusters = [];
      if (selectedIds?.length && Object.keys(propertyDataObjects).length) {
        Object.keys(propertyDataObjects).forEach(objectId => {
          propertyDataObjects[objectId].propertyData.forEach(cluster => {
            if (selectedIds.find(el => el.clusterID === cluster.clusterID)) {
              selectedClusters.push({ objectId: isNaN(parseInt(objectId)) ? objectId : parseInt(objectId), clusterID: cluster.clusterID });
            }
          })
        })
      }
      return selectedClusters;
    }
  ),

  exploreSelectedClusterNamesSelector: createSelector(
    state => state.exploreData,
    exploreData => {
      const { selectedIdsContext, propertyData } = exploreData;
      const selectedIds = selectedIdsContext?.ids;
      let selectedClusters = [];
      if (selectedIds?.length) {
        selectedClusters = propertyData.reduce((selectedArr, cluster) => {
          if (selectedIds.includes(cluster.clusterID) && cluster.clusterName !== null) {
            selectedArr.push(cluster.clusterName);
          }
          return selectedArr;
        }, []);
      }
      return selectedClusters;
    }
  ),

  explorePropertyDetailsDataSelector: createSelector(
    state => state.exploreData.propertyDetailsData,
    propertyDetailsData => propertyDetailsData
  ),

  isMainDataLoadingSelector: createSelector(
    (state, objectId) => {
      return state.exploreData.propertyDataObjects[objectId]?.isMainIndicatorVisible;
    },
    isMainIndicatorVisible => isMainIndicatorVisible
  ),

}